import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React from "react";

import { getQueryParameters } from "../url-params";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

const ISO_SHORT_DATE_FORMAT = "YYYY-MM-DD";

export type TemporalResolutionDuration = "day" | "week" | "month" | "quarter";
export type TemporalResolutionStartOf = "day" | "isoWeek" | "month" | "quarter";

export const useMount = (fn: () => void) => {
  React.useEffect(
    () => {
      fn();
    },
    // Want to simulate a mount effect here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

export type CustomTimeRange = {
  start: Dayjs;
  end: Dayjs;
};
export type TemporalResolution = {
  resolution: "DAILY" | "WEEKLY" | "MONTHLY" | "QUARTERLY";
  label: string;
  duration: TemporalResolutionDuration;
  startOf: TemporalResolutionStartOf;
  xAxisLabelFormat: string;
};

const TemporalResolutions: { [key: string]: TemporalResolution } = {
  DAILY: { resolution: "DAILY", label: "Daily", duration: "day", startOf: "day", xAxisLabelFormat: "D MMM" },
  WEEKLY: {
    resolution: "WEEKLY",
    label: "Weekly",
    duration: "week",
    startOf: "isoWeek",
    xAxisLabelFormat: "[Week] W (D MMM)",
  },
  MONTHLY: { resolution: "MONTHLY", label: "Monthly", duration: "month", startOf: "month", xAxisLabelFormat: "MMM" },
  QUARTERLY: {
    resolution: "QUARTERLY",
    label: "Quarterly",
    duration: "quarter",
    startOf: "quarter",
    xAxisLabelFormat: "MMM",
  }, // TODO xaxis
};

export type TimeResState = {
  selectedButtonIndex: number;
  temporalResolution: TemporalResolution;
  hideWeekly: boolean;
  hideMonthly: boolean;
};

const getResDataForTimeRange = (timeRange: CustomTimeRange): TimeResState => {
  const span = timeRange.end.diff(timeRange.start, "day");
  let temporalResolution, hideWeekly, hideMonthly;
  if (span <= 7) {
    hideMonthly = true;
    hideWeekly = true;
    temporalResolution = TemporalResolutions.DAILY;
  } else if (span <= 30) {
    hideMonthly = true;
    hideWeekly = false;
    temporalResolution = TemporalResolutions.DAILY;
  } else {
    hideMonthly = false;
    hideWeekly = false;
    temporalResolution = TemporalResolutions.WEEKLY;
  }
  return {
    temporalResolution,
    hideMonthly,
    hideWeekly,
    selectedButtonIndex: getSelectedButtonIndex(temporalResolution),
  };
};

type TimeData<T> = {
  timeRange: T;
  startDate: string | null;
  endDate: string | null;
};

const timeRangeExists = <T extends string>(timeRange: string, timeRangeLabels: { [key in T]?: string }) =>
  Object.keys(timeRangeLabels).includes(timeRange);

const getInitialTimeRange = <T extends string>(
  DEFAULT_DATE_RANGE: T,
  timeRangeLabels: { [key in T]?: string },
  sessionSuffix = "",
  initialDateRange: T = DEFAULT_DATE_RANGE as T,
  initialStartDate: string | null = null,
  initialEndDate: string | null = null
): TimeData<T> => {
  const queryParams = getQueryParameters();
  const [sessionTimeRange, sessionStartDate, sessionEndDate] = [
    window.sessionStorage.getItem(`tr${sessionSuffix}`),
    window.sessionStorage.getItem(`sd${sessionSuffix}`),
    window.sessionStorage.getItem(`ed${sessionSuffix}`),
  ];
  let [timeRange, startDate, endDate] = [initialDateRange, initialStartDate, initialEndDate];
  if (queryParams["tr"] && timeRangeExists(queryParams["tr"], timeRangeLabels)) {
    timeRange = queryParams["tr"] as T;
  } else {
    if (sessionTimeRange && timeRangeExists(sessionTimeRange, timeRangeLabels)) {
      timeRange = sessionTimeRange as T;
    }
  }
  if (timeRange === "CUSTOM") {
    if (isDateRangeValid(queryParams["sd"], queryParams["ed"])) {
      startDate = queryParams["sd"];
      endDate = queryParams["ed"];
    } else if (isDateRangeValid(sessionStartDate ?? undefined, sessionEndDate ?? undefined)) {
      startDate = sessionStartDate;
      endDate = sessionEndDate;
    } else {
      timeRange = DEFAULT_DATE_RANGE as T;
    }
  }
  return { timeRange, startDate, endDate };
};

const isDateRangeValid = (startDate: string | undefined, endDate: string | undefined) => {
  if (startDate && endDate) {
    const start = dayjs(startDate, ISO_SHORT_DATE_FORMAT, true);
    const end = dayjs(endDate, ISO_SHORT_DATE_FORMAT, true);
    return (
      start.isValid() &&
      end.isValid() &&
      start.format(ISO_SHORT_DATE_FORMAT) === startDate &&
      end.format(ISO_SHORT_DATE_FORMAT) === endDate &&
      start.isSameOrBefore(end)
    );
  } else {
    return false;
  }
};

const setQueryAndLocalStorage = <T extends string>(
  setQueryParameters: (object) => void,
  timeRange: T,
  startDate?: string,
  endDate?: string,
  sessionSuffix = ""
) => {
  const trSessionStorageKey = `tr${sessionSuffix}`;
  const sdSessionStorageKey = `sd${sessionSuffix}`;
  const edSessionStorageKey = `ed${sessionSuffix}`;

  if (timeRange === "CUSTOM" && startDate && endDate) {
    window.sessionStorage.setItem(trSessionStorageKey, timeRange);
    window.sessionStorage.setItem(sdSessionStorageKey, startDate);
    window.sessionStorage.setItem(edSessionStorageKey, endDate);
    setQueryParameters({
      tr: timeRange,
      sd: startDate,
      ed: endDate,
    });
  } else {
    window.sessionStorage.setItem(trSessionStorageKey, timeRange);
    window.sessionStorage.removeItem(sdSessionStorageKey);
    window.sessionStorage.removeItem(edSessionStorageKey);
    setQueryParameters({ tr: timeRange, sd: null, ed: null });
  }
};

const getSelectedButtonIndex = (selectedTemporalResolution: TemporalResolution) => {
  return !selectedTemporalResolution
    ? 1
    : selectedTemporalResolution.resolution === "MONTHLY"
      ? 2
      : selectedTemporalResolution.resolution === "WEEKLY"
        ? 1
        : 0;
};

export type TimeRangeAndTempResProps<D extends string> = {
  selectedTimeRange: CustomTimeRange;
  currentPreset: D;
  handleTimeRangeChange: (dateRangePreset: D, startDate?: string, endDate?: string) => void;
  temporalResolution: TemporalResolution;
  selectedButtonIndex: number;
  hideWeekly: boolean;
  hideMonthly: boolean;
  handleTempResChange: (res: string) => void;
};

export const useDateAndTempGeneric = <D extends string>(
  setQueryParameters: (object) => void,
  convertToTimeRange: (selectedDateRange: D, selectedStartDate?: string, selectedEndDate?: string) => CustomTimeRange,
  dateChangeCallback: (c: CustomTimeRange) => void = () => {},
  temporalResolutionChangeCallback: (t: TimeResState) => void = () => {},
  DEFAULT_DATE_RANGE: string,
  timeRangeLabels,
  sessionSuffix?: string
): TimeRangeAndTempResProps<D> => {
  const {
    timeRange: selectedDateRange,
    startDate: selectedStartDate,
    endDate: selectedEndDate,
  } = getInitialTimeRange(DEFAULT_DATE_RANGE as D, timeRangeLabels, sessionSuffix);

  useMount(() =>
    setQueryAndLocalStorage(
      setQueryParameters,
      selectedDateRange,
      selectedStartDate ?? undefined,
      selectedEndDate ?? undefined,
      sessionSuffix
    )
  );

  const [selectedDateRangeOption, setSelectedDateRangeOption] = React.useState<D>(selectedDateRange as D);

  const [selectedTimeRange, setSelectedTimeRange] = React.useState<CustomTimeRange>(
    convertToTimeRange(selectedDateRange as D, selectedStartDate ?? undefined, selectedEndDate ?? undefined)
  );

  const [timeResState, setTimeResState] = React.useState<TimeResState>(getResDataForTimeRange(selectedTimeRange));

  const savedDateCallback = React.useRef<((c: CustomTimeRange) => void) | null>(null);
  const savedTempCallback = React.useRef<((t: TimeResState) => void) | null>(null);
  React.useEffect(() => {
    savedDateCallback.current = dateChangeCallback;
  });
  React.useEffect(() => {
    savedTempCallback.current = temporalResolutionChangeCallback;
  });

  const handleTimeRangeChange = (newDateRangePreset: D, newStartDate?: string, newEndDate?: string) => {
    setQueryAndLocalStorage(setQueryParameters, newDateRangePreset, newStartDate, newEndDate, sessionSuffix);
    setSelectedDateRangeOption(newDateRangePreset);

    const currentTimeRange = convertToTimeRange(newDateRangePreset, newStartDate, newEndDate);
    if (selectedTimeRange !== currentTimeRange) {
      setSelectedTimeRange(currentTimeRange);
      savedDateCallback.current && savedDateCallback.current(currentTimeRange);

      const resDataForTimeRange = getResDataForTimeRange(currentTimeRange);
      if (resDataForTimeRange !== timeResState) {
        savedTempCallback.current && savedTempCallback.current(resDataForTimeRange);
      }
      setTimeResState(resDataForTimeRange);
    }
  };

  const handleTempResChange = (res: string) => {
    const resUpper = res.toUpperCase();
    const tempRes = TemporalResolutions[resUpper];
    const buttonIndex = getSelectedButtonIndex(tempRes);

    const tempResState = { ...timeResState, temporalResolution: tempRes, selectedButtonIndex: buttonIndex };

    savedTempCallback.current && savedTempCallback.current(tempResState);
    setTimeResState(tempResState);
  };

  return {
    selectedTimeRange,
    currentPreset: selectedDateRangeOption,
    handleTimeRangeChange,
    ...timeResState,
    handleTempResChange,
  };
};
